import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FolderOpen, Upload, Download, Trash2, FileText, Image, File } from "lucide-react";

const API = window.__API || "";

const KATEGORIEN = [
  { value: "pdf", label: "PDF" },
  { value: "plan", label: "Plan / Zeichnung" },
  { value: "vertrag", label: "Vertrag" },
  { value: "lieferschein", label: "Lieferschein" },
  { value: "anleitung", label: "Bedienungsanleitung" },
  { value: "foto", label: "Foto" },
  { value: "sonstiges", label: "Sonstiges" },
];

const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

export const DokumenteView = ({ projekte, auftraege }) => {
  const [docs, setDocs] = useState([]);
  const [filterProjekt, setFilterProjekt] = useState("");
  const [filterAuftrag, setFilterAuftrag] = useState("");
  const [filterKategorie, setFilterKategorie] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ projekt_id: "", auftrag_id: "", kategorie: "sonstiges", beschreibung: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadDocs = async () => {
    try {
      const params = {};
      if (filterProjekt) params.projekt_id = filterProjekt;
      if (filterAuftrag) params.auftrag_id = filterAuftrag;
      if (filterKategorie) params.kategorie = filterKategorie;
      const res = await axios.get(`${API}/api/dokumente/liste`, { params });
      setDocs(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadDocs(); }, [filterProjekt, filterAuftrag, filterKategorie]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Bitte eine Datei auswählen"); return; }
    if (!uploadData.projekt_id && !uploadData.auftrag_id) { toast.error("Bitte Projekt oder Auftrag angeben"); return; }

    setBusy(true);
    const formData = new FormData();
    formData.append("datei", uploadFile);
    formData.append("projekt_id", uploadData.projekt_id);
    formData.append("auftrag_id", uploadData.auftrag_id);
    formData.append("kategorie", uploadData.kategorie);
    formData.append("beschreibung", uploadData.beschreibung);

    try {
      await axios.post(`${API}/api/dokumente/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Dokument hochgeladen!");
      setShowUpload(false);
      setUploadFile(null);
      setUploadData({ projekt_id: "", auftrag_id: "", kategorie: "sonstiges", beschreibung: "" });
      loadDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Hochladen");
    }
    setBusy(false);
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/api/dokumente/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.dateiname);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error("Download fehlgeschlagen"); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/api/dokumente/${id}`);
      toast.success("Gelöscht");
      loadDocs();
    } catch (e) { toast.error("Fehler"); }
  };

  const getIcon = (dateityp) => {
    if (dateityp?.startsWith("image/")) return <Image className="w-4 h-4 text-green-500" />;
    if (dateityp?.includes("pdf")) return <FileText className="w-4 h-4 text-red-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const getProjektName = (id) => {
    const p = (projekte || []).find(x => x.id === id);
    return p ? p.name : "";
  };

  const getAuftragName = (id) => {
    const a = (auftraege || []).find(x => x.id === id);
    return a ? a.titel : "";
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <FolderOpen className="w-5 h-5" /> Dokumente
          </h3>
          <Button size="sm" onClick={() => setShowUpload(!showUpload)}>
            <Upload className="w-4 h-4 mr-1" /> Hochladen
          </Button>
        </div>

        {/* Upload form */}
        {showUpload && (
          <div className="border rounded p-3 mb-4 bg-gray-50 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label>Projekt (optional)</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={uploadData.projekt_id}
                  onChange={(e) => setUploadData({...uploadData, projekt_id: e.target.value})}
                >
                  <option value="">— Kein Projekt —</option>
                  {(projekte || []).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Auftrag (optional)</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={uploadData.auftrag_id}
                  onChange={(e) => setUploadData({...uploadData, auftrag_id: e.target.value})}
                >
                  <option value="">— Kein Auftrag —</option>
                  {(auftraege || []).map(a => (
                    <option key={a.id} value={a.id}>{a.titel} ({a.datum})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Kategorie</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={uploadData.kategorie}
                  onChange={(e) => setUploadData({...uploadData, kategorie: e.target.value})}
                >
                  {KATEGORIEN.map(k => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Beschreibung (optional)</Label>
                <Input
                  value={uploadData.beschreibung}
                  onChange={(e) => setUploadData({...uploadData, beschreibung: e.target.value})}
                  placeholder="z.B. Grundriss Erdgeschoss"
                />
              </div>
            </div>
            <div>
              <Label>Datei auswählen (max. 15 MB)</Label>
              <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} />
            </div>
            <Button onClick={handleUpload} disabled={busy} size="sm">
              <Upload className="w-4 h-4 mr-1" /> Jetzt hochladen
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <select
            className="border rounded px-2 py-1 text-sm"
            value={filterProjekt}
            onChange={(e) => { setFilterProjekt(e.target.value); setFilterAuftrag(""); }}
          >
            <option value="">Alle Projekte</option>
            {(projekte || []).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={filterAuftrag}
            onChange={(e) => { setFilterAuftrag(e.target.value); setFilterProjekt(""); }}
          >
            <option value="">Alle Aufträge</option>
            {(auftraege || []).map(a => (
              <option key={a.id} value={a.id}>{a.titel}</option>
            ))}
          </select>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={filterKategorie}
            onChange={(e) => setFilterKategorie(e.target.value)}
          >
            <option value="">Alle Kategorien</option>
            {KATEGORIEN.map(k => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>

        {/* Document list */}
        {docs.length === 0 ? (
          <p className="text-gray-400 text-sm">Noch keine Dokumente vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {docs.map(d => (
              <div key={d.id} className="flex items-center justify-between p-2 border rounded text-sm hover:bg-gray-50">
                <div className="flex items-center gap-2 flex-1">
                  {getIcon(d.dateityp)}
                  <div>
                    <p className="font-medium">{d.dateiname}</p>
                    <p className="text-xs text-gray-400">
                      {formatSize(d.groesse)} · {KATEGORIEN.find(k => k.value === d.kategorie)?.label || d.kategorie}
                      {d.projekt_id && ` · ${getProjektName(d.projekt_id)}`}
                      {d.auftrag_id && ` · ${getAuftragName(d.auftrag_id)}`}
                      {d.beschreibung && ` · ${d.beschreibung}`}
                    </p>
                    <p className="text-xs text-gray-300">{new Date(d.hochgeladen_am).toLocaleString("de-DE")}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} title="Herunterladen">
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteDoc(d.id)} title="Löschen">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
